import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tables } from "@/integrations/supabase/types";
import { formatDistanceToNow } from "date-fns";
import { CheckCircle2, User, Search, Phone, Mail, Archive, Calendar, DollarSign, Briefcase, MapPin, Car, Undo } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { EmailComposeDialog } from "@/components/admin/EmailComposeDialog";
import { SmsComposeDialog } from "@/components/admin/SmsComposeDialog";
import { findOrCreateCustomer, createDealFromPreApproval, createDealFromInquiry, logActivity } from "@/utils/customerSync";
import { logAudit } from "@/utils/auditLog";
import { useAuth } from "@/hooks/useAuth";

type ContactSubmission = Tables<"contact_submissions">;

export default function LeadsManagement() {
  const [leads, setLeads] = useState<ContactSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<ContactSubmission | null>(null);
  
  // Available vehicles and conversion dialog state
  const [availableVehicles, setAvailableVehicles] = useState<any[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>("");
  const [sellingPrice, setSellingPrice] = useState<string>("");
  const [isConvertDialogOpen, setIsConvertDialogOpen] = useState(false);
  const [leadToConvert, setLeadToConvert] = useState<ContactSubmission | null>(null);
  const [linkedVehicleStatus, setLinkedVehicleStatus] = useState<{ make: string, model: string, year: number, status: string } | null>(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [repFilter, setRepFilter] = useState("all");
  const [sortField, setSortField] = useState<"created_at" | "name" | "status">("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Bulk selection
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  
  const [profiles, setProfiles] = useState<{ id: string; full_name: string }[]>([]);
  const [convertingId, setConvertingId] = useState<string | null>(null);
  const [adminNote, setAdminNote] = useState("");
  const { user } = useAuth();
  
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);

  useEffect(() => {
    loadLeads();
    loadProfiles();
    fetchAvailableVehicles();
    const interval = setInterval(() => {
      loadLeads();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAvailableVehicles = async () => {
    const { data } = await supabase
      .from('vehicles')
      .select('id, year, make, model, stock_number, purchase_cost, price')
      .in('status', ['available', 'incoming']);
    if (data) setAvailableVehicles(data);
  };

  const loadProfiles = async () => {
    const { data } = await supabase.from("profiles").select("id, full_name");
    if (data) setProfiles(data);
  };

  const getRepName = (userId: string | null): string => {
    if (!userId) return 'Unassigned';
    return profiles.find(p => p.id === userId)?.full_name || 'Unknown';
  };

  const loadLeads = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("contact_submissions")
      .select("id, name, email, phone, message, status, assigned_to, notes, created_at")
      .not("message", "ilike", "Sell Your Car Request:%")
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Failed to load leads");
    } else {
      setLeads(data || []);
      if (selectedLead) {
        const updated = (data || []).find(l => l.id === selectedLead.id);
        if (updated) {
          setSelectedLead(updated);
          setAdminNote(getAdminNotes(updated.notes));
        } else {
          setSelectedLead(null);
        }
      }
    }
    setIsLoading(false);
  };

  const getAdminNotes = (notes: string | null): string => {
    if (!notes) return "";
    try {
      const parsed = JSON.parse(notes);
      return parsed._adminNotes || "";
    } catch {
      return notes;
    }
  };

  const updateLeadStatus = async (leadId: string, newStatus: string) => {
    const lead = leads.find(l => l.id === leadId);
    const oldStatus = lead?.status || 'unknown';
    const { error } = await supabase.from('contact_submissions').update({ status: newStatus }).eq('id', leadId);
    if (error) toast.error("Failed to update status");
    else {
      toast.success("Status updated");
      await logAudit({
        entityType: 'lead',
        entityId: leadId,
        action: 'status_changed',
        changes: { status: { from: oldStatus, to: newStatus } },
        metadata: { lead_name: lead?.name, email: lead?.email },
        performedBy: user?.id,
      });
      loadLeads();
    }
  };

  const handleAssignRep = async (leadId: string, repId: string) => {
    const lead = leads.find(l => l.id === leadId);
    const oldRep = lead?.assigned_to || null;
    const newRep = repId === 'unassigned' ? null : repId;
    const { error } = await supabase.from('contact_submissions').update({ assigned_to: newRep }).eq('id', leadId);
    if (error) toast.error('Failed to assign rep');
    else {
      toast.success('Rep assigned');
      await logAudit({
        entityType: 'lead',
        entityId: leadId,
        action: 'updated',
        changes: { assigned_to: { from: oldRep, to: newRep } },
        metadata: { lead_name: lead?.name },
        performedBy: user?.id,
      });
      loadLeads();
    }
  };

  const saveAdminNote = async () => {
    if (!selectedLead) return;
    const existingData = parsePreApprovalData(selectedLead.notes);
    const notesToSave = JSON.stringify({ ...existingData, _adminNotes: adminNote });

    const { error } = await supabase.from('contact_submissions').update({ notes: notesToSave }).eq('id', selectedLead.id);
    if (error) toast.error("Failed to save note");
    else {
      toast.success("Note saved");
      loadLeads();
    }
  };

  const parsePreApprovalData = (notes: string | null): any => {
    if (!notes) return null;
    try { return JSON.parse(notes); } catch { return null; }
  };

  const triggerConversion = async (lead: ContactSubmission) => {
    setLeadToConvert(lead);
    setLinkedVehicleStatus(null);
    if (lead.vehicle_id) {
      const linkedVeh = availableVehicles.find(v => v.id === lead.vehicle_id);
      if (linkedVeh) {
        setSelectedVehicleId(lead.vehicle_id);
        setSellingPrice(linkedVeh.price?.toString() || "");
      } else {
        setSelectedVehicleId("");
        setSellingPrice("");
        // Vehicle is not in available/incoming list, let's fetch it to see if it is sold
        const { data: veh } = await supabase.from('vehicles').select('make, model, year, status').eq('id', lead.vehicle_id).single();
        if (veh) {
          setLinkedVehicleStatus({
            make: veh.make,
            model: veh.model,
            year: veh.year,
            status: veh.status
          });
        }
      }
    } else {
      setSelectedVehicleId("");
      setSellingPrice("");
    }
    setIsConvertDialogOpen(true);
  };

  const completeConversion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadToConvert) return;
    if (!selectedVehicleId) {
      toast.error("Please select a vehicle to link to the deal.");
      return;
    }
    if (!sellingPrice || isNaN(Number(sellingPrice)) || Number(sellingPrice) <= 0) {
      toast.error("Please enter a valid positive selling price.");
      return;
    }

    setConvertingId(leadToConvert.id);
    try {
      const nameParts = leadToConvert.name.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || '-';

      const customerId = await findOrCreateCustomer({
        firstName,
        lastName,
        email: leadToConvert.email,
        phone: leadToConvert.phone || undefined
      });

      if (!customerId) {
        toast.error('Failed to create customer');
        return;
      }

      // Fetch the selected vehicle details to pass cost details
      const selectedVeh = availableVehicles.find(v => v.id === selectedVehicleId);
      const costPrice = selectedVeh ? (Number(selectedVeh.purchase_cost) || 0) : 0;
      const salePrice = Number(sellingPrice);
      const computedFrontGross = salePrice - costPrice;

      // Insert deal with all required details (prevents orphaned deals - MED-5)
      const { data: newDeal, error: dealError } = await supabase.from('deals').insert({
        customer_id: customerId,
        vehicle_id: selectedVehicleId,
        title: `Deal — ${selectedVeh ? `${selectedVeh.year} ${selectedVeh.make} ${selectedVeh.model}` : 'Vehicle'}`,
        stage: 'prospect',
        sale_price: salePrice,
        cost_price: costPrice,
        front_gross: computedFrontGross,
        notes: JSON.stringify({ comments: leadToConvert.message }),
        priority: 'medium',
      } as any).select('id, deal_number').single();

      if (dealError) throw dealError;

      const dealId = newDeal?.id || null;

      await supabase.from('contact_submissions').update({ customer_id: customerId, status: 'converted' }).eq('id', leadToConvert.id);

      await logActivity({
        customer_id: customerId,
        type: 'system',
        subject: 'Lead converted to deal',
        description: `Lead converted. Linked to vehicle ${selectedVeh?.year} ${selectedVeh?.make} ${selectedVeh?.model} (Stock #${selectedVeh?.stock_number}) with sale price $${salePrice.toLocaleString()}.`
      });

      await supabase.from("customer_interested_vehicles").insert({
        customer_id: customerId,
        vehicle_id: selectedVehicleId
      }).catch(() => {});

      // Log the conversion to audit log
      await logAudit({
        entityType: 'lead',
        entityId: leadToConvert.id,
        action: 'converted',
        metadata: {
          lead_name: leadToConvert.name,
          email: leadToConvert.email,
          customer_id: customerId,
          deal_id: dealId,
        },
        performedBy: user?.id,
      });

      // Log customer creation
      await logAudit({
        entityType: 'customer',
        entityId: customerId,
        action: 'created',
        metadata: { customer_name: leadToConvert.name, source: 'lead_conversion', lead_id: leadToConvert.id },
        performedBy: user?.id,
      });

      toast.success('Customer created and deal added to pipeline!');
      setIsConvertDialogOpen(false);
      setLeadToConvert(null);
      loadLeads();
      if (selectedLead?.id === leadToConvert.id) setSelectedLead(null);
    } catch (err: any) {
      toast.error('Failed to convert lead: ' + err.message);
    } finally {
      setConvertingId(null);
    }
  };

  const filteredLeads = leads.filter(l => {
    const matchesSearch = l.name.toLowerCase().includes(searchQuery.toLowerCase()) || l.email.toLowerCase().includes(searchQuery.toLowerCase()) || (l.phone && l.phone.includes(searchQuery));
    const matchesStatus = statusFilter === "all" ? l.status !== "archived" : l.status === statusFilter;
    const matchesRep = repFilter === "all" || (repFilter === "unassigned" ? !l.assigned_to : l.assigned_to === repFilter);
    return matchesSearch && matchesStatus && matchesRep;
  });

  const sortedLeads = [...filteredLeads].sort((a, b) => {
    let aVal: any;
    let bVal: any;

    if (sortField === "name") {
      aVal = a.name.toLowerCase();
      bVal = b.name.toLowerCase();
    } else if (sortField === "status") {
      aVal = a.status;
      bVal = b.status;
    } else { // default created_at
      aVal = new Date(a.created_at).getTime();
      bVal = new Date(b.created_at).getTime();
    }

    if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
    if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const handleBulkArchive = async () => {
    if (selectedLeadIds.length === 0) return;
    setIsBulkProcessing(true);
    try {
      const { error } = await supabase
        .from("contact_submissions")
        .update({ status: "archived" })
        .in("id", selectedLeadIds);

      if (error) throw error;

      await logAudit({
        entityType: "lead",
        entityId: "bulk",
        action: "updated",
        metadata: { count: selectedLeadIds.length, action: "bulk_archive" },
        performedBy: user?.id,
      });

      toast.success(`${selectedLeadIds.length} leads archived successfully`);
      setSelectedLeadIds([]);
      loadLeads();
    } catch (err: any) {
      toast.error("Failed to archive leads: " + err.message);
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleBulkAssignRep = async (repId: string) => {
    if (selectedLeadIds.length === 0) return;
    setIsBulkProcessing(true);
    try {
      const rep = profiles.find(p => p.id === repId);
      const { error } = await supabase
        .from("contact_submissions")
        .update({ assigned_to: repId || null })
        .in("id", selectedLeadIds);

      if (error) throw error;

      await logAudit({
        entityType: "lead",
        entityId: "bulk",
        action: "updated",
        metadata: { count: selectedLeadIds.length, assigned_to: rep?.full_name || "Unassigned" },
        performedBy: user?.id,
      });

      toast.success(`Assigned ${selectedLeadIds.length} leads to ${rep?.full_name || "unassigned"}`);
      setSelectedLeadIds([]);
      loadLeads();
    } catch (err: any) {
      toast.error("Failed to assign representative: " + err.message);
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const getStatusBadge = (status: string, leadId?: string) => {
    const colors: Record<string, string> = {
      new: "bg-blue-500 hover:bg-blue-600",
      contacted: "bg-yellow-500 hover:bg-yellow-600 text-yellow-950",
      processed: "bg-purple-500 hover:bg-purple-600",
      converted: "bg-green-500 hover:bg-green-600",
      archived: "bg-gray-500 hover:bg-gray-600"
    };
    return <Badge className={`${colors[status] || "bg-gray-500"} cursor-pointer transition-colors shadow-none font-semibold`} onClick={(e) => {
      e.stopPropagation();
      const next: Record<string, string> = { new: "contacted", contacted: "processed", processed: "new" };
      const targetId = leadId || selectedLead?.id;
      if (next[status] && targetId) updateLeadStatus(targetId, next[status]);
    }}>{status.toUpperCase()}</Badge>;
  };

  const renderPreApprovalData = (data: any) => {
    if (!data) return null;
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
        <div className="space-y-4">
          <div className="bg-muted/30 p-4 rounded-lg border">
            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3"><Car className="h-4 w-4 text-primary" /> Vehicle Preferences</h4>
            <div className="space-y-3 text-sm">
              <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">What type of vehicle are you interested in?</span> <span className="font-medium">{data.vehicleType || '-'}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">What is your budget?</span> <span className="font-medium">{data.budget || '-'}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">Do you have a trade-in?</span> <span className="font-medium">{data.tradeIn || '-'}</span></div>
            </div>
          </div>
          <div className="bg-muted/30 p-4 rounded-lg border">
            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3"><User className="h-4 w-4 text-primary" /> Personal Info</h4>
            <div className="space-y-3 text-sm">
              <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">Date of Birth</span> <span className="font-medium">{data.dateOfBirth ? `${data.dateOfBirth.slice(0,2)}/${data.dateOfBirth.slice(2,4)}/${data.dateOfBirth.slice(4,8)}` : '-'}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">Calculated Age</span> <span className="font-medium">{data.age || '-'}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">Social Insurance Number (SIN)</span> <span className="font-medium">{data.sin ? `***-**-${data.sin.slice(-4)}` : '-'}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">Current Address</span> <span className="font-medium break-words">{data.address || '-'}</span></div>
              {data.timeAtAddress && <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">Time at Address</span> <span className="font-medium">{data.timeAtAddress.years} years, {data.timeAtAddress.months} months</span></div>}
              {data.housing && <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">Housing Situation</span> <span className="font-medium">{data.housing.rentOrOwn === 'rent' ? 'Renting' : 'Own'} (${data.housing.monthlyPayment}/month)</span></div>}
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-muted/30 p-4 rounded-lg border">
            <h4 className="font-semibold text-sm flex items-center gap-2 mb-3"><Briefcase className="h-4 w-4 text-primary" /> Financial Profile</h4>
            <div className="space-y-3 text-sm">
              <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">Estimated credit rating</span> <span className="font-medium">{data.creditRating || '-'}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">Employment Status</span> <span className="font-medium">{data.employmentStatus || '-'}</span></div>
              
              {data.employerDetails && (
                <>
                  <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">Employer Name</span> <span className="font-medium">{data.employerDetails.name || '-'}</span></div>
                  <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">Job Title</span> <span className="font-medium">{data.employerDetails.jobTitle || '-'}</span></div>
                  {data.employerDetails.phone && <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">Employer Phone</span> <span className="font-medium">{data.employerDetails.phone}</span></div>}
                  {data.employerDetails.yearsEmployed && <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">Years Employed</span> <span className="font-medium">{data.employerDetails.yearsEmployed}</span></div>}
                </>
              )}
              
              {data.incomeDetails && (
                <>
                  <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">How would you like to provide your income information?</span> <span className="font-medium">{data.incomeDetails.type || '-'}</span></div>
                  
                  {data.incomeDetails.annualIncome && <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">What is your annual income before taxes & deductions?</span> <span className="font-medium">${parseInt(data.incomeDetails.annualIncome).toLocaleString()}</span></div>}
                  
                  {data.incomeDetails.monthlyIncome && <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">What is your monthly income before taxes & deductions?</span> <span className="font-medium">${parseInt(data.incomeDetails.monthlyIncome).toLocaleString()}</span></div>}
                  
                  {data.incomeDetails.hourlyWage && (
                    <>
                      <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">How much do you get paid per hour?</span> <span className="font-medium">${parseInt(data.incomeDetails.hourlyWage).toLocaleString()}</span></div>
                      <div className="flex flex-col"><span className="text-muted-foreground text-xs mb-1">Average hours per week</span> <span className="font-medium">{data.incomeDetails.hoursPerWeek}</span></div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 h-[calc(100vh-6rem)] flex flex-col">
      <div className="flex justify-between items-center shrink-0">
        <div>
          <h1 className="text-3xl font-heading font-bold">Lead Management</h1>
          <p className="text-muted-foreground">Manage and convert incoming inquiries</p>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 shrink-0">
        <Card><CardContent className="p-4 flex justify-between items-center"><div><p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Total Leads</p><p className="text-2xl font-bold">{leads.length}</p></div><User className="h-8 w-8 text-muted/20" /></CardContent></Card>
        <Card><CardContent className="p-4 flex justify-between items-center"><div><p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">New Today</p><p className="text-2xl font-bold text-blue-600">{leads.filter(l => new Date(l.created_at).toDateString() === new Date().toDateString()).length}</p></div><Calendar className="h-8 w-8 text-blue-600/20" /></CardContent></Card>
        <Card><CardContent className="p-4 flex justify-between items-center"><div><p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Pre-Approvals</p><p className="text-2xl font-bold text-purple-600">{leads.filter(l => l.message.includes("Pre-Approval")).length}</p></div><DollarSign className="h-8 w-8 text-purple-600/20" /></CardContent></Card>
        <Card><CardContent className="p-4 flex justify-between items-center"><div><p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Converted</p><p className="text-2xl font-bold text-green-600">{leads.filter(l => l.status === 'converted').length}</p></div><CheckCircle2 className="h-8 w-8 text-green-600/20" /></CardContent></Card>
      </div>

      <div className="flex flex-1 gap-6 overflow-hidden min-h-0">
        {/* Left Panel - List */}
        <div className={`w-full ${selectedLead ? 'hidden md:flex' : 'flex'} md:w-1/3 flex-col gap-4 overflow-hidden border rounded-xl bg-card shadow-sm`}>
          <div className="p-4 border-b space-y-3 bg-muted/30 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search name or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm" />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All (Active)</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="processed">Processed</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <Select value={repFilter} onValueChange={setRepFilter}>
                <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Assigned Rep" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reps</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Select value={sortField} onValueChange={(val) => setSortField(val as any)}>
                <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Sort By" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Date Created</SelectItem>
                  <SelectItem value="name">Lead Name</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortOrder} onValueChange={(val) => setSortOrder(val as any)}>
                <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Order" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Ascending</SelectItem>
                  <SelectItem value="desc">Descending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {selectedLeadIds.length > 0 && (
            <div className="p-3 bg-primary/5 border-b flex flex-col gap-2 shrink-0">
              <div className="flex justify-between items-center text-xs font-semibold text-muted-foreground">
                <span>{selectedLeadIds.length} Leads Selected</span>
                <Button variant="ghost" className="h-5 px-1.5 text-xs text-primary" onClick={() => setSelectedLeadIds([])}>Clear</Button>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={handleBulkArchive} disabled={isBulkProcessing}>
                  Archive Selected
                </Button>
                <Select onValueChange={handleBulkAssignRep} disabled={isBulkProcessing}>
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue placeholder="Assign Rep" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? <div className="p-8 text-center text-sm text-muted-foreground">Loading...</div> : 
             sortedLeads.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No leads match filters.</div> :
             sortedLeads.map(lead => (
               <div 
                 key={lead.id} 
                 className={`p-4 border-b cursor-pointer transition-colors hover:bg-muted/50 flex items-start gap-3 ${selectedLead?.id === lead.id ? 'bg-primary/5 border-l-4 border-l-primary' : 'border-l-4 border-l-transparent'}`}
               >
                 <Checkbox 
                   checked={selectedLeadIds.includes(lead.id)}
                   onCheckedChange={(checked) => {
                     if (checked) {
                       setSelectedLeadIds(prev => [...prev, lead.id]);
                     } else {
                       setSelectedLeadIds(prev => prev.filter(id => id !== lead.id));
                     }
                   }}
                   onClick={(e) => e.stopPropagation()}
                   className="mt-1"
                 />
                 <div className="flex-1 min-w-0" onClick={() => { setSelectedLead(lead); setAdminNote(getAdminNotes(lead.notes)); }}>
                   <div className="flex justify-between items-start mb-1">
                     <span className="font-semibold text-sm truncate pr-2">{lead.name}</span>
                     {getStatusBadge(lead.status, lead.id)}
                   </div>
                   <div className="text-xs text-muted-foreground mb-2 truncate">{lead.email}</div>
                   <div className="flex justify-between items-center text-xs">
                     <Badge variant="outline" className={`font-normal ${lead.message.includes('Pre-Approval') ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-slate-50'}`}>
                       {lead.message.includes('Pre-Approval') ? 'Pre-Approval' : 'General'}
                     </Badge>
                     <span className="text-muted-foreground">{formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}</span>
                   </div>
                 </div>
               </div>
             ))
            }
          </div>
        </div>

        {/* Right Panel - Detail */}
        {selectedLead ? (
          <div className="w-full md:w-2/3 flex flex-col bg-card border rounded-xl shadow-sm overflow-hidden min-h-0">
            {/* Detail Header */}
            <div className="p-6 border-b shrink-0 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-muted/10">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" className="md:hidden -ml-2" onClick={() => setSelectedLead(null)}>← Back</Button>
                <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xl font-bold">
                  {selectedLead.name.charAt(0)}
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{selectedLead.name}</h2>
                  <div className="text-sm text-muted-foreground flex gap-3 mt-1">
                    <span className="flex items-center"><Mail className="h-3 w-3 mr-1"/> {selectedLead.email}</span>
                    {selectedLead.phone && <span className="flex items-center"><Phone className="h-3 w-3 mr-1"/> {selectedLead.phone}</span>}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEmailDialogOpen(true)}>
                    <Mail className="h-4 w-4 mr-1"/> Email
                  </Button>
                  {selectedLead.phone && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        if (!selectedLead.customer_id) {
                          toast.error("Please convert this lead to a customer first to enable SMS features.");
                          triggerConversion(selectedLead);
                        } else {
                          setSmsDialogOpen(true);
                        }
                      }}
                    >
                      <Phone className="h-4 w-4 mr-1"/> SMS
                    </Button>
                  )}
                  {selectedLead.status !== 'converted' && selectedLead.status !== 'archived' && (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" disabled={convertingId === selectedLead.id} onClick={() => triggerConversion(selectedLead)}>
                      <CheckCircle2 className="h-4 w-4 mr-1"/> {convertingId === selectedLead.id ? 'Converting...' : 'Convert'}
                    </Button>
                  )}
                  {selectedLead.status !== 'archived' ? (
                    <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => updateLeadStatus(selectedLead.id, 'archived')}>
                      <Archive className="h-4 w-4 mr-1"/> Archive
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => updateLeadStatus(selectedLead.id, 'new')}>
                      <Undo className="h-4 w-4 mr-1"/> Restore
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm mt-1">
                  <span className="text-muted-foreground text-xs">Assign:</span>
                  <Select value={selectedLead.assigned_to || 'unassigned'} onValueChange={(v) => handleAssignRep(selectedLead.id, v)}>
                    <SelectTrigger className="h-7 text-xs w-[130px] border-none shadow-none bg-muted/50"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Detail Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-6">
                <h3 className="text-sm font-semibold uppercase text-muted-foreground tracking-wider mb-2">Original Message</h3>
                <div className="p-4 bg-muted/30 rounded-lg text-sm whitespace-pre-wrap border border-dashed">
                  {selectedLead.message}
                </div>
              </div>

              {selectedLead.message.includes('Pre-Approval') && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold uppercase text-muted-foreground tracking-wider mb-2">Pre-Approval Details</h3>
                  {renderPreApprovalData(parsePreApprovalData(selectedLead.notes))}
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-sm font-semibold uppercase text-muted-foreground tracking-wider mb-2 flex justify-between">
                  <span>Admin Notes</span>
                  <Button variant="ghost" size="sm" className="h-5 text-xs text-primary" onClick={saveAdminNote}>Save Notes</Button>
                </h3>
                <Textarea 
                  value={adminNote} 
                  onChange={e => setAdminNote(e.target.value)} 
                  placeholder="Private notes for the team..."
                  maxLength={2000}
                  className="min-h-[120px] bg-yellow-50/50 border-yellow-200 focus-visible:ring-yellow-400"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="hidden md:flex w-2/3 flex-col items-center justify-center bg-muted/10 border rounded-xl text-muted-foreground">
            <User className="h-16 w-16 mb-4 text-muted-foreground/30" />
            <p className="text-lg font-medium">Select a lead</p>
            <p className="text-sm">Choose a lead from the list to view details</p>
          </div>
        )}
      </div>

      {/* Communication Dialogs */}
      {selectedLead && (
        <>
          <EmailComposeDialog
            open={emailDialogOpen}
            onOpenChange={setEmailDialogOpen}
            recipientEmail={selectedLead.email}
            recipientName={selectedLead.name}
            customerId={selectedLead.customer_id}
            defaultSubject={selectedLead.message.includes('Pre-Approval') ? "Your Pre-Approval Application - Car Street" : "Following up on your inquiry - Car Street"}
          />
          {selectedLead.phone && selectedLead.customer_id && (
            <SmsComposeDialog
              open={smsDialogOpen}
              onOpenChange={setSmsDialogOpen}
              customerId={selectedLead.customer_id}
              customerName={selectedLead.name}
              customerPhone={selectedLead.phone}
            />
          )}
        </>
      )}
      {/* Convert Lead to Deal Dialog */}
      <Dialog open={isConvertDialogOpen} onOpenChange={(open) => { setIsConvertDialogOpen(open); if (!open) setLeadToConvert(null); }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Convert Lead to Pipeline Deal</DialogTitle>
          </DialogHeader>
          <form onSubmit={completeConversion} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Customer Details</Label>
              <div className="p-3 bg-muted rounded-md space-y-1 text-sm">
                <p><strong>Name:</strong> {leadToConvert?.name}</p>
                <p><strong>Email:</strong> {leadToConvert?.email}</p>
                {leadToConvert?.phone && <p><strong>Phone:</strong> {leadToConvert.phone}</p>}
              </div>
            </div>

            {linkedVehicleStatus && linkedVehicleStatus.status === "sold" && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                ⚠️ <strong>Warning:</strong> The vehicle requested in this inquiry ({linkedVehicleStatus.year} {linkedVehicleStatus.make} {linkedVehicleStatus.model}) is already marked as <strong>SOLD</strong>. Please select a different available vehicle below.
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="convert-vehicle">Select Vehicle *</Label>
              <Select value={selectedVehicleId} onValueChange={(value) => {
                setSelectedVehicleId(value);
                const selectedVeh = availableVehicles.find(v => v.id === value);
                if (selectedVeh) {
                  setSellingPrice(selectedVeh.price?.toString() || "");
                }
              }}>
                <SelectTrigger id="convert-vehicle">
                  <SelectValue placeholder="Select vehicle from inventory..." />
                </SelectTrigger>
                <SelectContent>
                  {availableVehicles.map(veh => (
                    <SelectItem key={veh.id} value={veh.id}>
                      {veh.year} {veh.make} {veh.model} (Stock #{veh.stock_number})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedVehicleId && (
              <div className="space-y-2">
                <Label htmlFor="convert-price">Negotiated Selling Price ($ CAD) *</Label>
                <Input
                  id="convert-price"
                  type="number"
                  placeholder="e.g. 24995"
                  value={sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value)}
                  required
                  min="1"
                />
                <p className="text-xs text-muted-foreground">
                  Pre-tax (HST-exclusive). Default vehicle price: ${availableVehicles.find(v => v.id === selectedVehicleId)?.price?.toLocaleString() || 'N/A'}.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsConvertDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white" disabled={convertingId === leadToConvert?.id}>
                {convertingId === leadToConvert?.id ? "Converting..." : "Convert Lead"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}